import FingerprintJS from "@fingerprintjs/fingerprintjs";
import { setFingerprint, setIsMobile } from "actions/appStateActions";
import { PageNotFound } from "components/ezpay/common/404-page-not-found";
import { ItemVoidedArchived } from "components/ezpay/common/item-voided-archived";
import { LinkExpired } from "components/ezpay/common/link-expired";
import { Menu } from "components/ezpay/common/menu";
import { PayableFooter } from "components/ezpay/common/payable-footer";
import { PayableHeader } from "components/ezpay/common/payable-header";
import { SandboxWarningModal, getCookieValue } from "components/ezpay/common/sandbox-warning-modal";
import { UnsubscribeAlerts } from "components/ezpay/common/unsubscribe-alerts";
import { WelcomeMessageSection } from "components/ezpay/common/welcome-message-section/welcome-message-section";
import AmountView from "components/ezpay/payables/amount-view/amount-view";
import { InvoiceListView } from "components/ezpay/payables/invoice-list-view";
import { LookupsView } from "components/ezpay/payables/lookups-view";
import { OrderView } from "components/ezpay/payables/order-view";
import { PayableDetails } from "components/ezpay/payables/payable-details";
import { PayableTypeCsrDirectLanding } from "components/ezpay/payables/payable-type-csr-direct-landing";
import { PaySlip } from "components/ezpay/retail-cash/pay-slip";
import { RetailerLocation } from "components/ezpay/retail-cash/retailer-location";
import { BalanceFrame } from "components/ezpay/secure/balance/balance-frame";
import { PayableRecentPayments } from "components/ezpay/secure/common/payable-recent-payments";
import { PaymentReceipt } from "components/ezpay/secure/common/payment-receipt";
import { RecentPayments } from "components/ezpay/secure/common/recent-payments";
import { RcpBarcodeView } from "components/ezpay/secure/common/retail-cash-flow/rcp-barcode-view";
import { SmsBarcodeView } from "components/ezpay/secure/common/retail-cash-flow/sms-barcode-view";
import { PayableCash } from "components/ezpay/secure/payable-cash";
import { PayableDashboard } from "components/ezpay/secure/payable-dashboard/payable-dashboard";
import { PayablePayment } from "components/ezpay/secure/payable-payment";
import { PayableReviewPage } from "components/ezpay/secure/payable-review-page";
import { Payment } from "components/ezpay/secure/payment";
import { PaymentSuccess } from "components/ezpay/secure/payment-success";
import { SelectPayableTypeView } from "components/ezpay/select-payable-type/select-payable-type-view";
import { CsrDirectLanding } from "components/ezpay/validate/csr-direct-landing";
import { DirectLanding } from "components/ezpay/validate/direct-landing";
import { PayableCsrDirectLanding } from "components/ezpay/validate/payable-csr-direct-landing";
import { SmsBarcodeDirectLanding } from "components/ezpay/validate/sms-barcode-direct-landing";
import { ValidationView } from "components/ezpay/validate/validation-view";
import Observability, {
  recordException,
} from "components/observability/observability";
import { PayableSessionProvider } from "components/payable-session-provider";
import { ProtectedRoute } from "components/protected-route";
import { PublicRoute } from "components/public-route";
import { SessionProvider } from "components/session-provider";
import { googleAnalyticsCode } from "config";
import { MENU_NAMESPACE } from "constants/i18n-namespaces";
import { EMPTY, ERROR } from "constants/Status";
import { ToastProvider } from "context";
import { useBiller, useLabels, useLabelsWithKrn } from "hooks";
import { t } from "i18next";
import {
  AnalyticsProvider,
  createAnalyticsInstance,
  useKubraTracking,
} from "kubra-ui-lib-mfe";
import { Banner, ForgeThemeProvider, Spinner } from "kubra-ux-forge";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useNavigate } from "react-router-dom";
import routing from "routing";
import { getBillerLoadStatus } from "selectors";
import { getDomain, mapUiLanguageToSessionLanguage } from "utilities/helper";

export const App = (): JSX.Element => {
  const [width, setWidth] = useState<number>(window.innerWidth);
  const [showSandboxModal, setShowSandboxModal] = useState(false);

  const { loadBiller, getBiller } = useBiller();
  const loadBillerStatus = useSelector(getBillerLoadStatus);
  const biller = getBiller();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (loadBillerStatus === EMPTY) {
      loadBiller();
    } else if (loadBillerStatus === ERROR) {
      console.log("failed to retrieve instance");
      navigate(routing.notFound.index());
    }
  }, [loadBillerStatus, loadBiller, navigate]);

  const { i18n } = useTranslation();
  const { loadLabels } = useLabels();
  const { loadLabelsWithKrn } = useLabelsWithKrn();

  useEffect(() => {
    if (!(biller?.clientId && biller?.realm && biller?.instanceId)) {
      return;
    }

    const domain = getDomain();
    const { clientId, realm, instanceId } = biller;
    const language = mapUiLanguageToSessionLanguage(
      i18n.resolvedLanguage ?? "en",
    );

    loadLabels(
      clientId,
      instanceId,
      domain,
      realm,
      language,
      biller.payableTypeDefinitions ?? [],
      biller.featureFlags?.payableUi ?? "false",
    );
    loadLabelsWithKrn(
      clientId,
      instanceId,
      domain,
      realm,
      language,
      biller.payableTypeDefinitions ?? [],
      biller.featureFlags?.payableUi ?? "false",
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [biller]);

  const handleWindowSizeChange = () => {
    setWidth(window.innerWidth);
  };

  const isMobile = width <= 768;
  const isLocal = process.env.REACT_APP_ENV === "local";

  useEffect(() => {
    const setFp = async () => {
      const fp = await FingerprintJS.load();
      const { visitorId } = await fp.get();
      dispatch(setFingerprint(visitorId));
      dispatch(setIsMobile(width <= 768));
    };

    setFp();

    window.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window.removeEventListener("resize", handleWindowSizeChange);
    };
  }, [navigate, biller, dispatch, width]);

  const analyticsInstance = useMemo(
    () => createAnalyticsInstance("EZ-PAY", googleAnalyticsCode),
    [],
  );

  const { Track } = useKubraTracking({
    commonTrackingData: { env: process.env.NODE_ENV, mfe: "EZ-PAY" },
    analytics: analyticsInstance,
  });

  // Centralized error handling - single spot for all errors
  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      recordException(event.error || new Error(event.message), {
        type: "GLOBAL_ERROR",
        source: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      recordException(event.reason, {
        type: "UNHANDLED_REJECTION",
      });
    };

    globalThis.addEventListener("error", handleError);
    globalThis.addEventListener("unhandledrejection", handleUnhandledRejection);

    return () => {
      globalThis.removeEventListener("error", handleError);
      globalThis.removeEventListener(
        "unhandledrejection",
        handleUnhandledRejection,
      );
    };
  }, []);

  useEffect(() => {
    if (biller?.realm.toLowerCase() === "sandbox") {
      const cookieValue = getCookieValue("dontshowSandboxWarning");
      const dontShow =
        cookieValue !== false && cookieValue.toLowerCase() === "true";
      setShowSandboxModal(!dontShow);
    }
  }, [biller]);

  // TODO: may be we should also wait until the labels are loaded?

  if (
    (biller === null || biller === undefined) &&
    location.pathname !== routing.notFound.index()
  ) {
    return <Spinner size="large" variant="primary" />;
  }

  return (
    // <IntlProvider locale={navigator.language} messages={messages[language]}>
    <div
      className={
        biller?.featureFlags?.payableUi?.toLowerCase() === "true"
          ? "ez-pay-background-payable"
          : "ez-pay-background-account"
      }
    >
      <AnalyticsProvider analytics={analyticsInstance}>
        <ForgeThemeProvider>
          {biller && !isLocal ? <Observability /> : <></>}
          <ToastProvider>
          {biller?.featureFlags?.payableUi?.toLowerCase() === "true" ? (
            <>
            <div className="ez-pay-header-container">
              {biller?.realm.toLowerCase() === "sandbox" && (
                <div className="app-banner-container">
                  <Banner
                    withIcon={true}
                    isMobile={isMobile}
                    messageLabel={t("sandbox-banner", { sandbox_name: biller?.billerName, ns: MENU_NAMESPACE })}
                    variant="warning"
                    data-cy="banner-warning-content"
                  />
                </div>  
              )}
              <div className="payable-header">
                <PayableHeader isMobile={isMobile} />
              </div>
            </div>
            <WelcomeMessageSection
                biller={biller}
                isMobile={isMobile}
              />
            </>
          ) : (
            <></>
          )}
          {biller?.featureFlags?.payableUi?.toLowerCase() === "true" && biller?.realm.toLowerCase() === "sandbox" && (
            <SandboxWarningModal
              open={showSandboxModal}
              closeSandbox={() => setShowSandboxModal(false)}
            />
          )}
          <Track>
            <Routes>
              {biller?.featureFlags?.payableUi?.toLowerCase() === "true" ? (
                // Payable UI is true, show the default routes for payable flow
                <Route element={<PayableSessionProvider />}>
                  <Route>
                    <Route
                      index
                      element={
                        <PublicRoute element={<SelectPayableTypeView />} />
                      }
                    />
                    <Route
                      path={routing.terms.index()}
                      element={
                        <PublicRoute element={<SelectPayableTypeView />} />
                      }
                    />
                    <Route
                      path={routing.privacy.index()}
                      element={
                        <PublicRoute element={<SelectPayableTypeView />} />
                      }
                    />
                    <Route
                      path={routing.lookup.index()}
                      element={<PublicRoute element={<LookupsView />} />}
                    />
                    <Route
                      path={routing.payableAmount.index()}
                      element={<PublicRoute element={<AmountView />} />}
                    />
                    <Route
                      path={routing.payableCreateOrder.index()}
                      element={<PublicRoute element={<OrderView />} />}
                    />
                    <Route
                      path={routing.unsubscribeAlerts.index() + "/*"}
                      element={<PublicRoute element={<UnsubscribeAlerts />} />}
                    />
                    <Route
                      path={routing.payableDetails.index()}
                      element={<ProtectedRoute element={<PayableDetails />} />}
                    />
                    <Route
                      path={routing.invoiceList.index()}
                      element={<ProtectedRoute element={<InvoiceListView />} />}
                    />
                    <Route
                      index
                      path={routing.payableDashboard.index()}
                      element={
                        <ProtectedRoute element={<PayableDashboard />} />
                      }
                    />
                    <Route
                      path={routing.payableReview.index()}
                      element={
                        <ProtectedRoute element={<PayableReviewPage />} />
                      }
                    />
                    <Route
                      path={routing.paymentSuccess.index()}
                      element={<ProtectedRoute element={<PaymentSuccess />} />}
                    />
                    <Route
                      path={routing.payableRecentPayments.index()}
                      element={
                        <ProtectedRoute element={<PayableRecentPayments />} />
                      }
                    />
                    <Route
                      path={routing.payableCash.index()}
                      element={<ProtectedRoute element={<PayableCash />} />}
                    />

                      <Route
                        path={routing.itemVoidedArchived.index()}
                        element={
                          <ProtectedRoute element={<ItemVoidedArchived />} />
                        }
                      />

                      {/* TODO: there's probably no need for separate components for direct landing.
                        The code inside these direct landing components can live in the PayableSession component. */}
                      <Route
                        path={routing.payableCsrDirectLanding.index() + "/*"}
                        element={<PayableCsrDirectLanding />}
                      />
                      <Route
                        path={routing.payableDirectLanding.index() + "/*"}
                        element={<PayablePayment />}
                      />
                      <Route
                        path={routing.payablePayment.index() + "/*"}
                        element={<PayablePayment />}
                      />
                    </Route>

                    <Route
                      path={routing.barcode.index() + "/*"}
                      element={<SmsBarcodeDirectLanding />}
                    />
                    <Route
                      path={routing.retailerLocation.index()}
                      element={<RetailerLocation />}
                    />
                    <Route
                      path={routing.payableTypeCsrDirectLanding.index()}
                      element={
                        <PublicRoute
                          element={<PayableTypeCsrDirectLanding />}
                        />
                      }
                    />
                  </Route>
                ) : (
                  // Payable UI is disabled, show the default routes for old account flow
                  <Route element={<SessionProvider />}>
                    <Route
                      path={routing.payslip.index()}
                      element={<PublicRoute element={<PaySlip />} />}
                    />
                    <Route
                      path={routing.retailerLocation.index()}
                      element={<RetailerLocation />}
                    />
                    <Route
                      path={routing.csrDirectLanding.index() + "/*"}
                      element={<CsrDirectLanding />}
                    />
                    <Route
                      path={routing.directLanding.index() + "/*"}
                      element={<DirectLanding />}
                    />
                    <Route
                      path={routing.barcode.index() + "/*"}
                      element={<SmsBarcodeDirectLanding />}
                    />

                    <Route element={<Menu isMobile={isMobile} />}>
                      <Route
                        index
                        element={
                          <PublicRoute
                            navToPath={routing.balance.index()}
                            element={<ValidationView />}
                          />
                        }
                      />
                      <Route
                        path={routing.terms.index()}
                        element={
                          <PublicRoute
                            navToPath={routing.balance.index()}
                            element={<ValidationView />}
                          />
                        }
                      />
                      <Route
                        path={routing.privacy.index()}
                        element={
                          <PublicRoute
                            navToPath={routing.balance.index()}
                            element={<ValidationView />}
                          />
                        }
                      />
                      <Route
                        path={routing.payment.index() + "/*"}
                        element={<PublicRoute element={<Payment />} />}
                      />
                      <Route
                        path={routing.balance.index()}
                        element={<ProtectedRoute element={<BalanceFrame />} />}
                      />
                      <Route
                        path={routing.paymentReceipt.index()}
                        element={
                          <ProtectedRoute element={<PaymentReceipt />} />
                        }
                      />
                      <Route
                        path={routing.unsubscribeAlerts.index() + "/*"}
                        element={
                          <PublicRoute element={<UnsubscribeAlerts />} />
                        }
                      />
                      <Route
                        path={routing.recentPayments.index()}
                        element={
                          <ProtectedRoute element={<RecentPayments />} />
                        }
                      />
                      <Route
                        path={routing.linkExpired.index()}
                        element={<LinkExpired></LinkExpired>}
                      />
                      <Route
                        path={routing.rcpBarcode.index()}
                        element={
                          <ProtectedRoute element={<SmsBarcodeView />} />
                        }
                      />
                      <Route
                        path={routing.rcpBarcodeView.index()}
                        element={
                          <ProtectedRoute element={<RcpBarcodeView />} />
                        }
                      />
                    </Route>
                  </Route>
                )}
                {/* 404 page */}
                <Route
                  path={routing.notFound.index()}
                  element={<PageNotFound />}
                />
                <Route path="/*" element={<PageNotFound />} />
              </Routes>
            </Track>
          </ToastProvider>
        </ForgeThemeProvider>
      </AnalyticsProvider>
      {biller?.featureFlags?.payableUi?.toLowerCase() === "true" ? (
        <div className="ez-pay-footer">
          <PayableFooter isMobile={isMobile} />
        </div>
      ) : null}
    </div>
  );
};
